import dayjs from 'dayjs';
import {
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { WorkOrderGroupChangeWebsocketDto } from 'src/models/work-order-state-change';
import {
  ApiFilterOperation,
  ResultDto,
  UserSettingsContext,
  WebsocketContext,
} from '@eas/common-web';
import { CalendarHandle } from '@modules/planner/calendar/calendar-types';
import {
  calendarSettingsId,
  calendarSettingsVersion,
} from '@modules/planner/calendar/hooks';
import { TableRowHandlersContext } from '@composite/table/table-row-handlers-context';
import {
  MobileUnit,
  WorkOrder,
  WorkOrderStateChangeWebsocketDto,
  ZsdUser,
} from '@models';
import { CalendarDimensions, EvidenceApiUrl } from '@enums';

export type CachedWorkOrder = {
  data: WorkOrder;
  remove?: boolean;
  id: string;
};

export const useListenToWorkOrderChanges = ({
  getRowId,
  currentDate,
  dimension,
  calendarRef,
  variant,
  cacheContentObserver,
}: {
  getRowId: (row: WorkOrder) => string;
  currentDate?: number;
  dimension?: CalendarDimensions;
  calendarRef?: RefObject<CalendarHandle>;
  variant: 'table' | 'calendar';
  cacheContentObserver?: (cache: Record<string, CachedWorkOrder>) => void;
}) => {
  /**
   * cachedRef holds all work orders that have changed since the last refresh
   * (ids received via websocket)
   */
  const cachedRef = useRef<{
    [key: string]: CachedWorkOrder;
  }>({});

  const { getCustomSettings } = useContext(UserSettingsContext);

  const { subscribe, unsubscribe } = useContext(WebsocketContext);
  const { updateRow } = useContext(TableRowHandlersContext);

  // calendar
  useImperativeHandle(
    calendarRef?.current?.clearWebsocketCachedData,
    () => () => {
      cachedRef.current = {};
    },
    []
  );

  // work ordet table
  const clearCache = useCallback(() => {
    cachedRef.current = {};
    cacheContentObserver?.(cachedRef.current);
  }, [cacheContentObserver]);

  const currentSettings = useMemo(
    () =>
      getCustomSettings(calendarSettingsId, calendarSettingsVersion) as {
        units?: (ZsdUser | MobileUnit)[];
      },
    [getCustomSettings]
  );

  useEffect(() => {
    const subscription = subscribe(
      '/topic/work-order/group-change',
      async (message) => {
        const msg = JSON.parse(
          message.body
        ) as WorkOrderGroupChangeWebsocketDto;

        if (!msg.workOrderIds) return;

        const response = await fetch(`${EvidenceApiUrl.WORK_ORDERS}/list`, {
          method: 'POST',
          headers: new Headers({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({
            size: -1,
            filters: [
              {
                field: 'id',
                operation: ApiFilterOperation.IN,
                values: msg.workOrderIds,
              },
            ],
          }),
        });
        if (!response.ok) {
          console.log(response);
          return;
        }
        const json = (await response.json()) as ResultDto<WorkOrder>;

        (json.items ?? []).forEach((changedWorkOrder) => {
          const data = {
            data: changedWorkOrder,
            remove: false,
            id: changedWorkOrder.id,
          };
          updateRow(getRowId?.(changedWorkOrder), data);
          cachedRef.current[changedWorkOrder.id] = data;
        });
        cacheContentObserver?.(cachedRef.current);
      }
    );
    return () => {
      if (subscription) {
        unsubscribe(subscription);
      }
    };
  }, [
    getRowId,
    subscribe,
    unsubscribe,
    updateRow,
    variant,
    cacheContentObserver,
  ]);

  useEffect(() => {
    const subscription = subscribe(
      '/topic/work-order/single-change',
      async (message) => {
        const msg = JSON.parse(
          message.body
        ) as WorkOrderStateChangeWebsocketDto;

        if (!msg.id) return;

        // const response = await fetch(`${EvidenceApiUrl.WORK_ORDERS}/${msg.id}`);
        // if (!response.ok) return;

        // call list endpoint instead of detail endpoint, to get technicalObjects[]
        const response = await fetch(`${EvidenceApiUrl.WORK_ORDERS}/list`, {
          method: 'POST',
          headers: new Headers({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({
            size: -1,
            filters: [
              {
                field: 'id',
                operation: ApiFilterOperation.IN,
                values: [msg.id],
              },
            ],
          }),
        });
        if (!response.ok) {
          console.log(response);
          return;
        }
        const json = (await response.json()) as ResultDto<WorkOrder>;

        const changedWorkOrder = json?.items?.[0] as WorkOrder;
        if (!changedWorkOrder) return;

        ///////////////

        let remove = false;

        // if we receive mobileUnitId or userId in websocket msg, it means that changed work order was unplanned from this user / MU
        const unplannedFrom = msg.mobileUnitId ?? msg.userId;

        if (variant === 'calendar' && unplannedFrom) {
          const data = {
            data: changedWorkOrder,
            id: changedWorkOrder.id,
            remove: true,
          };

          updateRow(unplannedFrom, data);

          cachedRef.current[changedWorkOrder.id] = data;
          cacheContentObserver?.(cachedRef.current);
          return;
        }

        if (variant === 'calendar') {
          const workOrderUnitId =
            changedWorkOrder.user?.id || changedWorkOrder.mobileUnit?.id;

          // do nothing if user / mobile unit is not in the calendar filter
          if (
            !currentSettings?.units?.find((unit) => unit.id === workOrderUnitId)
          ) {
            return;
          }

          if (currentDate) {
            const isVyk = changedWorkOrder.currentState?.code === 'VYK';
            // do nothing if changed WO endTime is before current calendar view start date
            if (
              dayjs(changedWorkOrder.endTime).isBefore(dayjs.unix(currentDate))
            ) {
              if (!isVyk) return;
              remove = true;
            }

            // do nothing if changed WO is after current calendar view end date
            if (
              dayjs(changedWorkOrder.startTime).isAfter(
                dayjs.unix(currentDate).add(1, dimension)
              )
            ) {
              if (!isVyk) return;
              remove = true;
            }
          }
        }
        ///////////////
        let preWorkOrder: WorkOrder | undefined = undefined;
        if (msg.preWorkOrderId) {
          const responsePre = await fetch(
            `${EvidenceApiUrl.WORK_ORDERS}/${msg.preWorkOrderId}`
          );
          if (responsePre.ok) {
            preWorkOrder = await responsePre.json();
          }
        }

        const data = {
          data: changedWorkOrder,
          remove,
          id: changedWorkOrder.id,
        };

        updateRow(getRowId?.(changedWorkOrder), data);

        cachedRef.current[changedWorkOrder.id] = data;

        if (preWorkOrder) {
          const preData = {
            data: preWorkOrder,
            remove: false,
            id: preWorkOrder.id,
          };
          updateRow(getRowId?.(preWorkOrder), preData);
          cachedRef.current[preWorkOrder.id] = preData;
        }
        cacheContentObserver?.(cachedRef.current);
      }
    );
    return () => {
      if (subscription) {
        unsubscribe(subscription);
      }
    };
  }, [
    subscribe,
    unsubscribe,
    updateRow,
    getRowId,
    variant,
    currentDate,
    dimension,
    currentSettings?.units,
    cacheContentObserver,
  ]);

  useEffect(() => {
    const subscription = subscribe(
      '/topic/work-order/ext-update',
      async (message) => {
        const id = message.body;

        if (!id) return;

        const response = await fetch(`${EvidenceApiUrl.WORK_ORDERS}/${id}`);
        if (!response.ok) return;

        const changedWorkOrder = (await response.json()) as WorkOrder;
        if (!changedWorkOrder) return;
        ///////////////
        const data = {
          data: changedWorkOrder,
          id: changedWorkOrder.id,
          remove: false,
        };

        updateRow(getRowId(changedWorkOrder), data);

        cachedRef.current[changedWorkOrder.id] = data;
        cacheContentObserver?.(cachedRef.current);
      }
    );
    return () => {
      if (subscription) {
        unsubscribe(subscription);
      }
    };
  }, [
    subscribe,
    unsubscribe,
    updateRow,
    getRowId,
    variant,
    cacheContentObserver,
  ]);

  const resendAllChangedWorkOrders = useCallback(() => {
    if (!Object.keys(cachedRef.current).length) return;

    (Object.values(cachedRef.current) as CachedWorkOrder[]).forEach(
      (cachedWorkOrder) => {
        updateRow(getRowId(cachedWorkOrder.data), cachedWorkOrder);
      }
    );
  }, [updateRow, getRowId]);

  const refreshRow = useCallback(
    (rowId: string) => {
      if (!Object.keys(cachedRef.current).length) return;

      Object.values(cachedRef.current).forEach((cachedWorkOrder) => {
        const id = getRowId(cachedWorkOrder.data);
        if (id === rowId) updateRow(id, cachedWorkOrder);
      });
    },
    [updateRow, getRowId]
  );

  return {
    resendAllChangedWorkOrders,
    refreshRow,
    clearCache,
    cachedRef,
  };
};
